const HYDRATION_CACHE = {}
import { LitElement, html, Splide } from "./-global.min.js"

class HHProductCard extends LitElement {
  static properties = {
    shouldHydrate: {
      type: Boolean,
      attribute: "hydrate",
    },
    inSlider: {
      type: String,
      attribute: "in-slider",
    },
    productHandle: {
      type: String,
      attribute: "product-handle",
    },
    enableCarousel: {
      type: Boolean,
      attribute: "enable-carousel",
    },
  }

  get priceWrapper() {
    return this.querySelector(".js-card-price")
  }

  get anchors() {
    return this.querySelectorAll(".js-anchor")
  }

  get variantButtons() {
    return [...this.querySelectorAll(".js-swatch")]
  }

  get images() {
    return this.querySelectorAll(".card__media img")
  }

  constructor() {
    super()

    this.inSlider = ""
    this.splides = []
  }

  connectedCallback() {
    super.connectedCallback()
    if (this.shouldHydrate) {
      return this._hydrate()
    }
    if (this.enableCarousel) this.updateDisplayMode()
    this._attachedListeners()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._removeListeners()
  }

  _attachedListeners() {
    this.variantButtons.forEach((button) => {
      button.addEventListener("click", this.onClickHandler.bind(this))
    })
    this.images.forEach((button) => {
      button.addEventListener("keyup", this.onKeyupImageHandler.bind(this))
    })
  }

  _removeListeners() {
    this.variantButtons.forEach((button) => {
      button.removeEventListener("click", this.onClickHandler)
    })
    this.images.forEach((button) => {
      button.removeEventListener("keyup", this.onKeyupImageHandler)
    })
  }

  updateDisplayMode() {
    if (
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    ) {
      this.initCarousel()
    } else {
      this.destroyCarousel()
    }
  }

  initCarousel() {
    const mediaContainers = this.querySelectorAll(".card__media--gallery")
    if (!mediaContainers.length) return

    mediaContainers.forEach((container) => {
      const splide = new Splide(container, {
        type: "slide",
        arrows: false,
        pagination: true,
        drag: true,
        perMove: 1,
        focus: "center",
        snap: true,
      })

      splide.mount()
      this.splides.push(splide)
    })
  }

  destroyCarousel() {
    if (this.splides.length > 0) {
      this.splides.forEach((splide) => splide.destroy())
      this.splides = []
    }
  }

  async _hydrate() {
    const cacheVar = HYDRATION_CACHE

    if (cacheVar[this.productHandle]) {
      this.innerHTML = cacheVar[this.productHandle]
    } else {
      const viewParam = "card"
      const request = await fetch(
        `${window.routes.root_url}products/${this.productHandle}?view=${viewParam}`
      )
      const container = document.createElement("div")
      const returnedHtml = await request.text()
      container.innerHTML = returnedHtml
      cacheVar[this.productHandle] =
        container.querySelector("hh-product-card").innerHTML
      this.innerHTML = cacheVar[this.productHandle]
    }

    this._attachedListeners()
    if (this.inSlider) {
      this.closest(this.inSlider).scrollTo({
        detail: {
          index: 0,
        },
      })
      setTimeout(() => this.closest(this.inSlider).updateUI(), 50)
    }
  }

  async onClickHandler(event) {
    event.preventDefault()

    // 1. Mark the card as "interacted with"
    this.classList.add("is-swatched")

    this.anchors.forEach(
      (item) => (item.href = event.target.dataset.variantUrl)
    )

    this.hideAllVariantImages()
    this.showVariantImage(event)
    this.showVariantPrice(event)

    this.variantButtons.forEach((el) => {
      el.classList.remove("active")
    })

    event.target.classList.add("active")
  }

  onKeyupImageHandler(event) {
    if (event.type === "keyup" && event.code.toUpperCase() !== "ENTER") {
      return true
    }
    event && event.preventDefault()
    const href = event.target.closest("a").getAttribute("href") || ""
    if (href !== "" && href !== "#") {
      location.href = href
    }
  }

  async hideAllVariantImages() {
    this.querySelectorAll("[data-variant-swatch]").forEach((element) => {
      element.classList.remove("active")
      element
        .querySelectorAll("img")
        .forEach((img) => img.removeAttribute("tabindex"))
    })
  }

  async showVariantImage(event) {
    const vname = event.target.dataset.variantName
    const selector = `[data-variant-swatch="${vname}"]`
    const swatch = this.querySelector(selector)

    if (swatch) {
      swatch.classList.add("active")

      // Find all images in this variant container
      const allImgs = swatch.querySelectorAll("img")
      const hiddenImg = swatch.querySelector('img[data-hidden="true"]')
      const primaryImg = swatch.querySelector('img:not([data-hidden="true"])')

      // If swatched, give focus to the "hidden" image (which CSS will show)
      // Otherwise, give focus to the primary image
      if (this.classList.contains("is-swatched") && hiddenImg) {
        hiddenImg.setAttribute("tabindex", "0")
        if (primaryImg) primaryImg.removeAttribute("tabindex")
      } else if (primaryImg) {
        primaryImg.setAttribute("tabindex", "0")
      }
    }

    if (this.enableCarousel && this.splides.length > 0) {
      this.splides.forEach((splide) => {
        splide.go(0)
      })
    }
  }

  showVariantPrice(event) {
    const price_html = event.target.dataset.price
    if (this.priceWrapper) this.priceWrapper.innerHTML = price_html
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define("hh-product-card", HHProductCard)
